import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { todaySastDateStr } from '@/lib/planShopping';

// The whole plan, Day 1 to Day N, as GET /programs/my-plan returns it (#330).
// It answers before the plan starts too, which /programs/my-menu's week view
// never could without the client knowing where Day 1 is.

export type PlanPhase = 'UPCOMING' | 'IN_PROGRESS' | 'FINISHED';

export interface PlanMeal {
    /** Stable for this meal on this date. For React keys and sets only. */
    key: string;
    date: string;
    type: 'program' | 'custom';
    programMealId?: string;
    customMealId?: string;
    mealType: string;
    sortOrder: number;
    name: string;
    recipe: { id: string; name: string; imageUrl?: string | null; servings?: number } | null;
    servings: number;
    isOptional: boolean;
    isSubstituted: boolean;
    isCompleted: boolean;
    /** false: shopping for it adds nothing (free-text custom meal, or no ingredients). */
    hasIngredients: boolean;
}

export interface PlanDay {
    dayNumber: number;
    date: string;
    name: string;
    meals: PlanMeal[];
}

export interface MyPlan {
    assignmentId: string;
    status: string;
    phase: PlanPhase;
    startDate: string;
    endDate: string;
    currentDayNumber: number;
    daysUntilStart: number;
    duration: number;
    program: { id: string; name: string; description?: string | null; imageUrl?: string | null };
    days: PlanDay[];
}

export const getMyPlan = async (today: string): Promise<MyPlan | null> => {
    const response = await api.get<{ plan: MyPlan | null }>('/programs/my-plan', {
        params: { today },
    });
    return response.data?.plan ?? null;
};

export const useMyPlan = () => {
    // Always the SAST calendar date: without it the server falls back to its
    // UTC date, which is still yesterday between 00:00 and 02:00 SAST.
    const today = todaySastDateStr();
    return useQuery({
        queryKey: ['my-plan', today],
        queryFn: () => getMyPlan(today),
    });
};

// POST /programs/my-menu/shopping-list with a selection (#330).

export interface SelectedPlanMeal {
    date: string;
    programMealId?: string;
    customMealId?: string;
    servings?: number;
}

export interface PlanShoppingPayload {
    startDate: string;
    endDate: string;
    selectedMeals: SelectedPlanMeal[];
    name?: string;
    shoppingListId?: string;
    includeMealsAlreadyOnList?: boolean;
}

export interface MergeSummary {
    mode: 'created' | 'merged';
    added: number;
    increased: number;
    unticked: number;
    skipped: {
        itemId: string;
        ingredientId: string;
        unit: string;
        amount: number;
        reason: string;
    }[];
}

export interface PlanShoppingWarning {
    code: 'RANGE_OVERLAP' | 'MEALS_NOT_ON_MENU' | string;
    message: string;
    action?: 'SKIPPED' | 'ADDED_AGAIN';
    overlapDates?: string[];
}

export interface PlanShoppingResult {
    id: string;
    name: string;
    items: unknown[];
    unlistedMeals?: { date: string; mealType: string; name: string }[];
    merge?: MergeSummary;
    warnings?: PlanShoppingWarning[];
}

export const generatePlanShoppingList = async (
    payload: PlanShoppingPayload,
): Promise<PlanShoppingResult> => {
    const response = await api.post<PlanShoppingResult>('/programs/my-menu/shopping-list', payload);
    return response.data;
};

export const useGeneratePlanShoppingList = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: generatePlanShoppingList,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
            // A merge changes the target list too, and its page caches it under
            // its own key with a 5 minute stale time.
            queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
        },
    });
};
